import { useEffect, useMemo, useState } from "react";

import { fetchHalls, searchShowtimes } from "../api";
import {
  DEPTH_LABELS,
  LAYOUT_LABELS,
  SIDE_LABELS,
  STEP_LABELS,
  capacityRange,
  defaultCriteria,
  hallSizeChoices,
  isCompact,
  matchesHallSize,
  reconcile,
  stepsFor,
  zonesLabel,
  type Criteria,
  type StepKey,
} from "../search";
import type { Bounds, Hall, Movie, SearchResponse } from "../types";
import { Dialog } from "./Dialog";
import { HallStage } from "./wizard/HallStage";
import { DepthStep, FilmStep, GroupStep, RecapStep, SideStep, SizeStep } from "./wizard/Steps";

type Props = {
  cinemaSlug: string;
  date: string;
  movies: Movie[];
  initialMovieSlug: string;
  onResults: (response: SearchResponse, criteria: Criteria) => void;
  onClose: () => void;
};

const TITLES: Record<StepKey, [string, string]> = {
  film: ["Quel film ?", "Choisissez le film à voir ce jour-là."],
  size: ["Taille de salle", "Le plan au-dessus montre une salle de ce cinéma, à l'échelle."],
  depth: ["Où s'asseoir", "Cochez les zones qui vous conviennent — elles s'allument sur la salle."],
  side: ["Placement latéral", "Au centre, sur les côtés, ou les deux."],
  group: ["Combien et comment", "Vos places s'affichent sur la salle."],
  recap: ["Récapitulatif", "Vérifiez vos critères et lancez la recherche."],
};

export function SearchWizard({
  cinemaSlug,
  date,
  movies,
  initialMovieSlug,
  onResults,
  onClose,
}: Props) {
  const [criteria, setCriteria] = useState<Criteria>(() => defaultCriteria(initialMovieSlug));
  const [step, setStep] = useState<StepKey>("film");
  const [halls, setHalls] = useState<Hall[]>([]);
  const [bounds, setBounds] = useState<Bounds>(null);
  const [hallIndex, setHallIndex] = useState(0);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchHalls(cinemaSlug, date)
      .then((catalog) => {
        if (cancelled) return;
        setHalls(catalog.halls);
        setBounds(catalog.bounds);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [cinemaSlug, date]);

  const movie = movies.find((item) => item.slug === criteria.movieSlug) ?? movies[0];
  const steps = useMemo(() => stepsFor(movie), [movie]);
  const current = steps.includes(step) ? step : "film";
  const index = steps.indexOf(current);

  /** Le carrousel ne montre que les salles du palier choisi. */
  const stageHalls = useMemo(() => {
    const inSize = halls.filter((hall) => matchesHallSize(hall.capacity, criteria.hallSize, bounds));
    return inSize.length > 0 ? inSize : halls;
  }, [halls, criteria.hallSize, bounds]);

  useEffect(() => setHallIndex(0), [criteria.hallSize, stageHalls.length]);

  const update = (patch: Partial<Criteria>) => {
    setError(null);
    setCriteria((previous) => {
      const next = { ...previous, ...patch };
      const target = movies.find((item) => item.slug === next.movieSlug) ?? movie;
      return reconcile(next, target, bounds);
    });
  };

  const candidates = movie.showtimes.filter((showtime) =>
    matchesHallSize(showtime.capacity ?? 0, criteria.hallSize, bounds),
  ).length;

  const runSearch = async () => {
    setSearching(true);
    setError(null);
    try {
      const response = await searchShowtimes(cinemaSlug, {
        date,
        movieSlug: criteria.movieSlug,
        depth: criteria.depth,
        side: criteria.side,
        people: criteria.people,
        layout: criteria.layout,
        ...capacityRange(bounds, criteria.hallSize),
      });
      onResults(response, criteria);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSearching(false);
    }
  };

  const goNext = () => (current === "recap" ? void runSearch() : setStep(steps[index + 1]));
  const goPrevious = () => (index === 0 ? onClose() : setStep(steps[index - 1]));

  const valueOf = (key: StepKey) => {
    if (key === "film") return movie.title;
    if (key === "size")
      return hallSizeChoices(movie, bounds).find((c) => c.value === criteria.hallSize)?.label ?? "";
    if (key === "depth") return zonesLabel(criteria.depth, DEPTH_LABELS, "Toute la salle");
    if (key === "side") return zonesLabel(criteria.side, SIDE_LABELS, "Toute la largeur");
    if (key === "group")
      return `${criteria.people} · ${criteria.people >= 2 ? LAYOUT_LABELS[criteria.layout] : "seul"}`;
    return `${candidates} séance${candidates > 1 ? "s" : ""}`;
  };

  // On laisse tout décocher, mais on ne passe pas à la suite sans zone retenue.
  const blocked =
    (current === "depth" && criteria.depth.length === 0) ||
    (current === "side" && criteria.side.length === 0) ||
    (current === "recap" && candidates === 0);

  const [title, subtitle] = TITLES[current];
  const showStage = current !== "film";

  return (
    <Dialog
      title="Trouver la meilleure place"
      subtitle={`Étape ${index + 1} sur ${steps.length}`}
      width="wide"
      onClose={onClose}
      bodyless
      footer={
        searching ? (
          <div className="progress">
            <span className="progress-label" aria-live="polite">
              lecture des {candidates} plans de salle… jusqu'à une dizaine de secondes la
              première fois
            </span>
            <div className="bar">
              <span className="bar-fill" style={{ width: "100%", opacity: 0.4 }} />
            </div>
          </div>
        ) : (
          <>
            <button type="button" className="btn btn-ghost" onClick={goPrevious}>
              {index === 0 ? "Fermer" : "Retour"}
            </button>
            {error && <span className="state is-error">{error}</span>}
            {!error && blocked && current !== "recap" && (
              <span className="state">Choisissez au moins une zone.</span>
            )}
            <span className="grow" />
            <button
              type="button"
              className="btn btn-primary"
              onClick={goNext}
              disabled={blocked}
            >
              {current === "recap"
                ? `Lancer la recherche · ${candidates} séance${candidates > 1 ? "s" : ""}`
                : "Suivant"}
            </button>
          </>
        )
      }
    >
      <div className="wizard">
        <nav className="wizard-rail">
          {steps.map((key, position) => (
            <button
              key={key}
              type="button"
              className={`rail-step${key === current ? " is-current" : position < index ? " is-done" : ""}`}
              onClick={() => setStep(key)}
            >
              <span className="rail-num">{position + 1}</span>
              <span style={{ minWidth: 0 }}>
                {STEP_LABELS[key]}
                <span className="rail-value">{valueOf(key)}</span>
              </span>
            </button>
          ))}
        </nav>

        <div className="wizard-main">
          <div className="wizard-dots">
            {steps.map((key, position) => (
              <i key={key} className={position <= index ? "is-on" : ""} />
            ))}
          </div>

          {showStage && (
            <HallStage
              halls={stageHalls}
              index={hallIndex}
              onIndex={setHallIndex}
              criteria={criteria}
              showGroup={current === "group" || current === "recap"}
            />
          )}

          <div className="wizard-panel">
            <h3 className="wizard-title">{title}</h3>
            <p className="wizard-sub">{subtitle}</p>

            {current === "film" && <FilmStep movies={movies} criteria={criteria} update={update} />}
            {current === "size" && (
              <SizeStep movie={movie} bounds={bounds} criteria={criteria} update={update} />
            )}
            {current === "depth" && <DepthStep movie={movie} criteria={criteria} update={update} />}
            {current === "side" && <SideStep criteria={criteria} update={update} />}
            {current === "group" && <GroupStep criteria={criteria} update={update} />}
            {current === "recap" && (
              <RecapStep
                movie={movie}
                criteria={criteria}
                bounds={bounds}
                compact={isCompact(movie)}
                candidates={candidates}
              />
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}
